import React from "react";
import { gql, ApolloConsumer } from "@apollo/client";
import {
  Input,
  Button,
  Modal,
  notification,
  message,
  Steps,
  Card,
  Row,
  Col,
  Checkbox,
  DatePicker,
  Select,
  Radio,
  Form,
} from "antd";
import type { FormInstance, UploadFile } from "antd";
import {
  FileAddOutlined,
  AppstoreOutlined,
  ControlOutlined,
  ContainerOutlined,
  RocketOutlined,
  BuildOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { findDOMNode } from "react-dom";
import { client } from "../App";
import migrationSources from "../lib/migrationSources";
import { SourceOption, SourceSystem } from "../lib/migrationSource";
import {
  advancedMigrationRecordTypes,
  basicMigrationRecordTypes,
  recordTypeName,
} from "../lib/recordTypes";
import AgentSelect from "./createMigrationRun/AgentSelect";
import ProjectSelect from "./createMigrationRun/ProjectSelect";
import UserSelect from "./createMigrationRun/UserSelect";
import MigrationSelect from "./createMigrationRun/MigrationSelect";
import properCase from "../lib/properCase";
import { MigrationFileUploader } from "./MigrationFileUploader";
import momentGenerateConfig from "rc-picker/lib/generate/moment";
import { GeniusRetailDestinationOptions } from "./createMigrationRun/GeniusRetailDestinationOptions";
import { PmPlusSourceOptions } from "./createMigrationRun/PmPlusSourceOptions";

const MomentDatePicker =
  DatePicker.generatePicker<moment.Moment>(momentGenerateConfig);

const { Meta } = Card;

export const CREATE_MIGRATION_RUN_MUTATION = gql`
  mutation createMigrationRun(
    $projectId: ID!
    $migrationName: String!
    $recordTypes: [String!]!
    $scheduledStartTime: String!
    $sourceType: String!
    $sourceSchemaName: String
    $sourceSubdomain: String
    $sourceApiToken: String
    $agentToken: String
    $destinationType: String!
    $destinationSubdomain: String
    $destinationAccountId: String
    $migrationRunType: String!
    $productionSubdomain: String
    $userId: ID
    $signOffDueByDate: String
    $sendClientEmails: Boolean
    $salesHistoryFilterDate: String
    $createTrainingTenant: Boolean
    $migrationFiles: [MigrationFilePartialInput]
  ) {
    createMigrationRun(
      projectId: $projectId
      migrationName: $migrationName
      recordTypes: $recordTypes
      scheduledStartTime: $scheduledStartTime
      sourceType: $sourceType
      sourceSchemaName: $sourceSchemaName
      sourceSubdomain: $sourceSubdomain
      sourceApiToken: $sourceApiToken
      agentToken: $agentToken
      destinationType: $destinationType
      destinationSubdomain: $destinationSubdomain
      destinationAccountId: $destinationAccountId
      migrationRunType: $migrationRunType
      productionSubdomain: $productionSubdomain
      userId: $userId
      signOffDueByDate: $signOffDueByDate
      sendClientEmails: $sendClientEmails
      salesHistoryFilterDate: $salesHistoryFilterDate
      createTrainingTenant: $createTrainingTenant
      migrationFiles: $migrationFiles
    ) {
      id
    }
  }
`;

const MIGRATION_RUN_QUERY = gql`
  query migrationRun($id: ID!) {
    migrationRun(id: $id) {
      projectId
      migrationName
      recordTypes
      scheduledStartTime
      sourceType
      agentToken
      sourceSubdomain
      sourceApiToken
      sourceSchemaName
      destinationType
      destinationSubdomain
      destinationAccountId
      migrationRunType
      productionSubdomain
      userId
      signOffDueByDate
      sendClientEmails
      salesHistoryFilterDate
      createTrainingTenant
      migrationFiles {
        filename
        url
      }
    }
  }
`;

export const MIGRATION_SOURCES_QUERY = gql`
  query {
    sources {
      type
      recordTypes
    }
  }
`;

const DESTINATION_RECORD_TYPES_QUERY = gql`
  query destinationRecordTypes($type: String!) {
    destinationRecordTypes(type: $type)
  }
`;

const { Step } = Steps;
const { Option } = Select;
const formLayout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 14 },
};

const stepOrder = {
  Source: 1,
  Options: 2,
  Files: 3,
  Records: 4,
  Review: 5,
};

function defaultState() {
  return {
    migrationId: null,
    modalTitle: "Create new migration run",
    currentStep: 0,
    stepNames: [],
    visible: false,
    migrationFiles: [],
    confirmLoading: false,
    migrationSourceFields: {},
    recordTypeOption: "custom",
    recordTypes: [],
    sourceRecordTypes: [],
    projectId: null,
    projectName: null,
    migrationName: null,
    scheduledStartTime: moment().seconds(0).milliseconds(0),
    sourceType: null,
    agentToken: null,
    sourceSubdomain: null,
    sourceApiToken: null,
    destinationType: null,
    destinationSubdomain: null,
    destinationRecordTypes: [],
    migrationRunType: "preview",
    productionSubdomain: null,
    userId: null,
    signOffDueByDate: moment().add(7, "days").seconds(0).milliseconds(0),
    sendClientEmails: false,
    salesHistoryFilterDate: moment()
      .seconds(0)
      .milliseconds(0)
      .subtract(3, "years"),
    createTrainingTenant: false,
    nextDisabled: false,
    backDisabled: false,
  };
}

type CreateMigrationRunButtonProps = {
  buttonText?: string;
  style?: React.CSSProperties;
  buttonStyle?: React.CSSProperties;
  migrationSearchVal?: string;
};

export class CreateMigrationRunButton extends React.Component<CreateMigrationRunButtonProps> {
  state = defaultState();
  formRef = React.createRef<FormInstance>();
  projectSelectRef = React.createRef<any>();
  checkBoxDivRef = React.createRef<any>();

  componentDidMount() {
    const { destinationType } = this.state;
    this.handleSetDestinationType(destinationType);
  }

  showModal = () => {
    this.setState({
      visible: true,
    });
  };

  resetModal = () => {
    const state = defaultState();
    state.visible = true;
    this.setState(state);
  };

  closeAndResetModal = () => {
    this.setState(defaultState());
  };

  nextStep = () => {
    let { currentStep } = this.state;
    currentStep += 1;
    if (this.formRef.current) {
      this.formRef.current
        .validateFields()
        .then(() => {
          this.setState({
            currentStep,
          });
        })
        .catch(() => {
          message.error("Option form failed validation");
        });
    } else {
      this.setState({ currentStep });
    }
  };

  previousStep = () => {
    let { currentStep } = this.state;
    currentStep -= 1;
    if (currentStep === 0) {
      this.resetModal();
    } else {
      this.setState({ currentStep });
    }
  };

  startOver = () => {
    const state = defaultState();
    state.visible = true;
    this.setState(state);
  };

  handleCancel = () => {
    this.closeAndResetModal();
  };

  handleOk = () => {
    this.setState({
      confirmLoading: true,
    });
    setTimeout(() => {
      client
        .mutate({
          mutation: CREATE_MIGRATION_RUN_MUTATION,
          variables: this.requestVariables(),
        })
        .then(() => {
          notification["success"]({
            message: "Successfully created new Migration Run",
          });
          this.closeAndResetModal();
        })
        .catch((err) => {
          notification["error"]({
            message: "Failed to create Migration Run",
            description: err.message,
          });
        });
      this.setState({
        confirmLoading: false,
      });
    }, 2000);
  };

  handleRecordCheckbox = (selectedRecordTypes) => {
    this.setState({
      recordTypes: selectedRecordTypes,
      recordTypeOption: "custom",
    });
  };

  handleProjectSelectChange = async (projectId: string, company: string) => {
    const suggestedSubdomain = company
      .toString()
      .replace(/\W/g, "")
      .toLocaleLowerCase();
    this.setState(
      {
        projectId: projectId,
        projectName: company,
        destinationSubdomain: suggestedSubdomain,
      },
      async () => {
        this.updateProductionSubdomain();
        this.updateMigrationName();
      },
    );
  };

  handleSpringboardSubdomainChange = async (subdomain: string) => {
    this.setState(
      {
        destinationSubdomain: subdomain,
      },
      () => {
        this.updateProductionSubdomain();
      },
    );
  };

  handleMigrationRunTypeChange = async (migrationRunType: string) => {
    const { destinationSubdomain } = this.state;
    let state: {};
    if (migrationRunType === "preview") {
      state = {
        migrationRunType: migrationRunType,
        productionSubdomain: null,
        signOffDueByDate: moment().seconds(0).milliseconds(0).add(7, "days"),
      };
    } else if (migrationRunType === "production") {
      state = {
        migrationRunType: migrationRunType,
        productionSubdomain: destinationSubdomain,
        createTrainingTenant: false,
        signOffDueByDate: null,
      };
    }
    this.setState(state, () => {
      this.updateMigrationName();
    });
  };

  componentDidUpdate = () => {
    let { recordTypes, migrationRunType, createTrainingTenant } = this.state;
    if (
      recordTypes.includes("customFields") &&
      (migrationRunType === "production" || createTrainingTenant === true)
    ) {
      notification["warning"]({
        message:
          "Unable to create Production/Training run with Custom Fields record",
        description:
          "The custom fields record type has automatically been unselected. Make sure you have already done a preview build with custom fields before this run.",
      });
      recordTypes = recordTypes.filter((record) => record !== "customFields");
      this.setState({ recordTypes });
    }
  };

  validRecordTypes = (): any[] => {
    const { sourceRecordTypes, destinationRecordTypes } = this.state;
    return sourceRecordTypes
      .map((record) => {
        if (destinationRecordTypes.includes(record)) {
          return record;
        }
        return null;
      })
      .filter(Boolean);
  };

  handleSetRecordTypes = (e) => {
    const validRecordTypes = this.validRecordTypes();
    const changeType = e.target.value;
    let recordTypes = [];
    if (changeType === "all") {
      recordTypes = validRecordTypes;
    } else if (changeType === "none") {
      recordTypes = [];
    } else if (changeType === "advanced") {
      recordTypes = validRecordTypes.filter((recordType) =>
        advancedMigrationRecordTypes.includes(recordType),
      );
    } else if (changeType === "basic") {
      recordTypes = validRecordTypes.filter((recordType) =>
        basicMigrationRecordTypes.includes(recordType),
      );
    }

    this.setState({
      recordTypes: recordTypes,
      recordTypeOption: changeType,
    });
  };

  handleSetSourceType = async (sourceType: string, sourceName: string) => {
    this.getSourceRecordTypes(sourceType).then((sourceRecordTypes) => {
      const modalTitle = `Create new ${sourceName} migration run`;
      this.setState({
        sourceRecordTypes: sourceRecordTypes,
        sourceType: sourceType,
        recordTypes: [],
        modalTitle: modalTitle,
      });
      this.nextStep();
    });
  };

  handleSetDestinationType = async (type: string) => {
    const destinationRecordTypes = await this.getDestinationRecordTypes(type);
    this.setState({ destinationRecordTypes });
  };

  getDestinationRecordTypes = async (type: string) => {
    const response = await client.query({
      query: DESTINATION_RECORD_TYPES_QUERY,
      variables: { type },
    });
    return response.data.destinationRecordTypes;
  };

  getSourceRecordTypes = async (sourceType): Promise<string[]> => {
    return client.query({ query: MIGRATION_SOURCES_QUERY }).then((response) => {
      const source = response.data.sources.find(
        (source) => source.type === sourceType,
      );
      return source.recordTypes;
    });
  };

  handleFormValuesChange = (changedVals, _vals) => {
    this.setState(changedVals, () => {
      if (changedVals.projectId) {
        let projectId = changedVals.projectId;
        const projectSelectNode = findDOMNode(this.projectSelectRef.current)
          .childNodes[1];
        const company = projectSelectNode.textContent;
        this.handleProjectSelectChange(projectId, company).then(() => {
          this.formRef.current.setFieldsValue(this.state);
        });
      }
      if (changedVals.destinationType) {
        this.handleSetDestinationType(changedVals.destinationType);
      }
      if (changedVals.destinationSubdomain) {
        let subdomain = changedVals.destinationSubdomain;
        this.handleSpringboardSubdomainChange(subdomain).then(() => {
          this.formRef.current.setFieldsValue(this.state);
        });
      }
      if (changedVals.migrationRunType) {
        let runType = changedVals.migrationRunType;
        this.handleMigrationRunTypeChange(runType).then(() => {
          this.formRef.current.setFieldsValue(this.state);
        });
      }
    });
  };

  handleFileUploaded = (file: UploadFile<any>) => {
    let { migrationFiles } = this.state;
    migrationFiles = migrationFiles.filter((f) => f.filename !== file.name);
    migrationFiles.push({ filename: file.name, url: file.url });
    migrationFiles.sort((a, b) => a.filename.localeCompare(b.filename));
    this.setState({ migrationFiles });
  };

  handleFileRemoved = (file: UploadFile<any>) => {
    let { migrationFiles } = this.state;
    migrationFiles = migrationFiles.filter((f) => f.filename !== file.name);
    this.setState({ migrationFiles: migrationFiles });
  };

  updateProjectName = async () => {
    const { projectName } = this.state;
    if (!projectName) {
      const projectSelectNode = findDOMNode(this.projectSelectRef.current);
      const newProjectName = projectSelectNode.textContent;
      await this.setState({ projectName: newProjectName });
    }
  };

  updateMigrationName = async () => {
    this.updateProjectName().then(() => {
      const { migrationRunType, projectName } = this.state;
      const migrationName = `${projectName} - ${properCase(migrationRunType)}`;
      this.setState({
        migrationName: migrationName,
      });
    });
  };

  updateProductionSubdomain = async () => {
    const { migrationRunType, destinationSubdomain } = this.state;
    if (migrationRunType === "production") {
      this.setState({ productionSubdomain: destinationSubdomain });
    }
  };

  requestVariables = () => {
    const definitions = CREATE_MIGRATION_RUN_MUTATION.definitions[0];
    let variables = {};
    definitions["variableDefinitions"].forEach((def) => {
      let key = def.variable.name.value;
      variables[key] = this.state[key];
    });
    return variables;
  };

  isMissingFiles = (recordTypes: string[]): boolean => {
    let isMissingFiles = false;
    recordTypes.forEach((recordType) => {
      const missingFiles = this.missingRecordTypeFiles(recordType);
      if (missingFiles.length > 0) {
        isMissingFiles = true;
      }
    });
    return isMissingFiles;
  };

  missingRecordTypeFiles = (recordType: string): string[] => {
    const { sourceType, migrationFiles } = this.state;
    const migrationSource = migrationSources[sourceType];
    if (!migrationSource || !migrationSource.requiresFileUpload) {
      return [];
    }
    const requiredFiles = migrationSource.requiredRecordFiles[recordType];
    if (!requiredFiles) {
      return [];
    }
    const missingFiles = [];
    requiredFiles.forEach((filename) => {
      const matchingFiles = migrationFiles.filter(
        (f) => f.filename === filename,
      );
      if (matchingFiles.length < 1) {
        missingFiles.push(filename);
      }
    });
    return missingFiles;
  };

  recordTypeCheckboxes = (): JSX.Element[] => {
    const { recordTypes, sourceRecordTypes, destinationRecordTypes } =
      this.state;
    const checkboxes = sourceRecordTypes.map((recordType) => {
      const warningTexts: JSX.Element[] = [];
      let disabled = false;
      if (recordTypes.includes(recordType)) {
        const missingFiles = this.missingRecordTypeFiles(recordType);
        if (missingFiles.length > 0) {
          warningTexts.push(
            <span key="file-error" style={{ paddingLeft: 10, color: "red" }}>
              {`Missing files: ${missingFiles.join(", ")}`}
            </span>,
          );
        }
      }
      if (!destinationRecordTypes.includes(recordType)) {
        disabled = true;
        warningTexts.push(
          <span key="dest-error" style={{ paddingLeft: 10, color: "red" }}>
            Destination does not support this type
          </span>,
        );
      }
      return (
        <Col key={recordType} span={24}>
          <Checkbox value={recordType} disabled={disabled}>
            {recordTypeName(recordType)} {warningTexts}
          </Checkbox>
        </Col>
      );
    });
    return checkboxes;
  };

  onMigrationRunFetched = async (migrationRun) => {
    const { scheduledStartTime, signOffDueByDate } = this.state;
    const state = Object.assign({}, migrationRun);
    const source = migrationSources[migrationRun["sourceType"]];
    const modalTitle = `Create new ${source.name} migration run`;
    state["scheduledStartTime"] = scheduledStartTime;
    state["signOffDueByDate"] = signOffDueByDate;
    state["modalTitle"] = modalTitle;
    state["sourceRecordTypes"] = await this.getSourceRecordTypes(
      migrationRun.sourceType,
    );
    state["destinationRecordTypes"] = await this.getDestinationRecordTypes(
      migrationRun.destinationType,
    );
    if (migrationRun["salesHistoryFilterDate"]) {
      state["salesHistoryFilterDate"] = moment(
        migrationRun["salesHistoryFilterDate"],
      )
        .seconds(0)
        .milliseconds(0);
    }
    if (source.requiresFileUpload) {
      state["migrationFiles"] = migrationRun.migrationFiles.map((file) => {
        return {
          filename: file.filename,
          url: file.url,
        };
      });
    } else {
      state["migrationFiles"] = [];
    }
    this.setState(state);
    this.nextStep();
  };

  migrationSelect = (migrationId: string) => {
    const { visible } = this.state;
    let shouldAutoOpen = false;
    if (this.props.migrationSearchVal && visible) {
      shouldAutoOpen = true;
    }
    return (
      <ApolloConsumer>
        {(client) => (
          <MigrationSelect
            style={{ width: "100%", marginBottom: 20 }}
            defaultOpen={shouldAutoOpen}
            autoFocus={shouldAutoOpen}
            value={migrationId}
            searchValue={this.props.migrationSearchVal}
            onChange={async (migrationId) => {
              client
                .query({
                  query: MIGRATION_RUN_QUERY,
                  variables: { id: migrationId },
                })
                .then((response) => {
                  this.onMigrationRunFetched(response.data.migrationRun);
                })
                .catch((err) => {
                  message.error("Failed to get migration run");
                  console.error(err);
                });
            }}
          />
        )}
      </ApolloConsumer>
    );
  };

  systemListPage = () => {
    const { migrationId } = this.state;
    return (
      <div>
        <label>Copy from previous migration run</label>
        {this.migrationSelect(migrationId)}
        <div style={{ paddingLeft: 10, height: "100%" }}>
          <Row gutter={[20, 50]}>
            {Object.keys(migrationSources).map((sourceType, index) => {
              const source = migrationSources[sourceType];
              return (
                <Col span={12} key={index}>
                  <Card
                    hoverable
                    onClick={() =>
                      this.handleSetSourceType(sourceType, source.name)
                    }
                    style={{
                      width: 225,
                      height: 120,
                    }}
                    styles={{ body: { padding: 10, textAlign: "center" } }}
                    cover={
                      <div
                        className="source-card"
                        style={{
                          width: 225,
                          height: 120,
                          alignItems: "center",
                          display: "flex",
                        }}
                      >
                        {source.beta && (
                          <img
                            alt="beta banner"
                            src={"/images/beta-banner.png"}
                            style={{
                              maxHeight: "75%",
                              position: "absolute",
                              top: 0,
                              right: -1,
                            }}
                          />
                        )}
                        <img
                          alt="source logo"
                          src={source.logo}
                          style={{
                            maxHeight: "100%",
                            maxWidth: "100%",
                            padding: 25,
                          }}
                        />
                      </div>
                    }
                  >
                    <Meta
                      title={`${source.name}${source.beta ? " (beta)" : ""}`}
                    />
                  </Card>
                </Col>
              );
            })}
          </Row>
        </div>
      </div>
    );
  };

  sourceRecordTypesPage = () => {
    const { sourceType, sourceRecordTypes } = this.state;
    if (!sourceType) {
      return;
    }
    const radios = ["Advanced", "Basic", "All", "None", "Custom"].map(
      (value) => (
        <Radio.Button
          key={value}
          value={value.toLowerCase()}
          data-source-records={sourceRecordTypes}
        >
          {value}
        </Radio.Button>
      ),
    );
    const checkboxes = this.recordTypeCheckboxes();
    return (
      <div ref={this.checkBoxDivRef}>
        <div>
          <Radio.Group
            value={this.state.recordTypeOption}
            buttonStyle="solid"
            onChange={this.handleSetRecordTypes}
            style={{ paddingBottom: 20 }}
          >
            {" "}
            {radios}
          </Radio.Group>
        </div>
        <Checkbox.Group
          style={{ width: "100%" }}
          onChange={this.handleRecordCheckbox}
          value={this.state.recordTypes}
        >
          <Row>{checkboxes}</Row>
        </Checkbox.Group>
      </div>
    );
  };

  sourceOptionFormFields = (
    sourceFields: SourceOption[] | null,
  ): JSX.Element[] => {
    if (sourceFields == null) {
      return [];
    }
    const fields = sourceFields.map((field) => {
      if (field.type === "string") {
        return (
          <Form.Item
            key={field.key}
            name={field.key}
            label={field.name}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
        );
      } else if (field.type === "boolean") {
        return (
          <Form.Item
            key={field.key}
            name={field.key}
            label={field.name}
            valuePropName="checked"
          >
            <Checkbox />
          </Form.Item>
        );
      } else if (field.type === "date") {
        return (
          <Form.Item
            key={field.key}
            name={field.key}
            label={field.name}
            rules={[{ required: true }]}
          >
            <MomentDatePicker style={{ width: "100%" }} />
          </Form.Item>
        );
      } else if (field.type === "list") {
        return (
          <Form.Item
            key={field.key}
            name={field.key}
            label={field.name}
            rules={[{ required: true }]}
          >
            <Select style={{ width: "100%" }}>
              {field.listOptions.map((val) => (
                <Option key={val} value={val}>
                  {val}
                </Option>
              ))}
            </Select>
          </Form.Item>
        );
      } else if (field.type === "element") {
        return (
          <Form.Item key={field.key} name={field.key} label={field.name}>
            {field.element}
          </Form.Item>
        );
      }
      return null;
    });
    return fields;
  };

  destinationOptions = (): JSX.Element[] => {
    const options = [
      <Option key="sr-direct" value="sr-direct">
        Heartland Retail
      </Option>,
      <Option key="genius-retail" value="genius-retail">
        Genius Retail
      </Option>,
      <Option key="csv" value="csv">
        CSV
      </Option>,
    ];

    if (process.env.NODE_ENV === "development") {
      options.push(
        <Option key="dev-log" value="dev-log">
          Developer Log
        </Option>,
      );
    }

    return options;
  };

  migrationRunOptionFields = (): JSX.Element[] => {
    const { destinationType } = this.state;
    const options = [
      <h3
        key="migrationOptionHeader"
        style={{ color: "rgba(255, 255, 255, 0.45)" }}
      >
        Migration Run Options
      </h3>,
      <Form.Item
        key="projectId"
        name="projectId"
        label="Migration"
        rules={[{ required: true }]}
      >
        <ProjectSelect ref={this.projectSelectRef} />
      </Form.Item>,
      <Form.Item
        key="userId"
        name="userId"
        label="Migration Rep"
        rules={[{ required: true }]}
      >
        <UserSelect />
      </Form.Item>,
      <Form.Item
        key="scheduledStartTime"
        name="scheduledStartTime"
        label="Scheduled Start Time"
        rules={[{ required: true }]}
      >
        <MomentDatePicker
          showTime
          showNow
          style={{ width: "100%" }}
          format="M/D/YYYY h:mm a"
        />
      </Form.Item>,
    ];

    if (destinationType === "sr-direct") {
      options.push(
        <Form.Item
          key="sendClientEmails"
          name="sendClientEmails"
          label="Send Client Emails"
          valuePropName="checked"
        >
          <Checkbox />
        </Form.Item>,
      );
    }

    return options;
  };

  sourceOptionFields = (sourceSystem: SourceSystem): JSX.Element[] => {
    let options = [
      <h3
        key="sourceOptionHeader"
        style={{ color: "rgba(255, 255, 255, 0.45)" }}
      >
        Source Options
      </h3>,
      <Form.Item
        key="salesHistoryFilterDate"
        name="salesHistoryFilterDate"
        label="Sales History Filter Date"
        rules={[{ required: true }]}
      >
        <MomentDatePicker style={{ width: "100%" }} format="M/D/YYYY" />
      </Form.Item>,
    ];

    if (sourceSystem.requiresAgent) {
      options.push(
        <Form.Item
          key="agentToken"
          name="agentToken"
          label="Agent"
          rules={[{ required: true }]}
        >
          <AgentSelect />
        </Form.Item>,
      );
    }

    options = options.concat(this.sourceOptionFormFields(sourceSystem.options));

    if (sourceSystem.name === "PM+") {
      options.push(
        <PmPlusSourceOptions key="pm-plus-source-options" />
      );
    }

    return options;
  };

  destinationOptionFields = (): JSX.Element[] => {
    const { migrationRunType, destinationType } = this.state;

    let options = [
      <h3
        key="destinationOptionHeader"
        style={{ color: "rgba(255, 255, 255, 0.45)" }}
      >
        Destination Options
      </h3>,
      <Form.Item
        key="destinationType"
        name="destinationType"
        label="Destination"
      >
        <Select style={{ width: "100%" }} value="sr-direct">
          {this.destinationOptions()}
        </Select>
      </Form.Item>,
    ];

    if (destinationType === "genius-retail") {
      options.push(
        <GeniusRetailDestinationOptions key="genius-retail-destination-options" />
      );
    }

    if (destinationType === "sr-direct") {
      options = options.concat([
        <Form.Item
          key="migrationRunType"
          name="migrationRunType"
          label="Run Type"
          rules={[{ required: true }]}
        >
          <Radio.Group
            buttonStyle="solid"
            style={{ width: "100%", textAlign: "center" }}
          >
            <Radio.Button style={{ width: "50%" }} value="preview">
              Preview {<BuildOutlined />}
            </Radio.Button>
            <Radio.Button style={{ width: "50%" }} value="production">
              Production {<RocketOutlined />}
            </Radio.Button>
          </Radio.Group>
        </Form.Item>,
        <Form.Item
          key="destinationSubdomain"
          name="destinationSubdomain"
          label="Destination Subdomain"
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>,
      ]);

      if (migrationRunType === "production") {
        options.push(
          <Form.Item
            key="productionSubdomain"
            name="productionSubdomain"
            label="Production Subdomain"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>,
        );
      } else if (migrationRunType === "preview") {
        options = options.concat([
          <Form.Item
            key="signOffDueByDate"
            name="signOffDueByDate"
            label="Sign Off Due By"
          >
            <MomentDatePicker
              showToday
              style={{
                width: "100%",
              }}
              format="M/D/YYYY h:mm a"
            />
          </Form.Item>,
          <Form.Item
            key="createTrainingTenant"
            name="createTrainingTenant"
            label="Create Training Tenant"
            valuePropName="checked"
          >
            <Checkbox />
          </Form.Item>,
        ]);
      }
    }

    return options;
  };

  formOptionFields = (sourceSystem: SourceSystem): JSX.Element[] => {
    if (sourceSystem === undefined) {
      return [];
    }
    return this.migrationRunOptionFields().concat(
      this.sourceOptionFields(sourceSystem),
      this.destinationOptionFields(),
    );
  };

  reviewPage = () => {
    const { sourceType } = this.state;
    const source = migrationSources[sourceType];
    const variables = this.requestVariables();
    const details = [];
    for (let key in variables) {
      if (key === "migrationFiles" && source && !source.requiresFileUpload) {
        continue;
      }
      let val = variables[key];
      if (val || val === false) {
        if (key === "migrationFiles") {
          val = val.map((f) => f.filename).join(", ");
        }
        details.push(
          <p key={key}>
            <b>{properCase(key)}</b>: {`${val}`}
          </p>,
        );
      }
    }
    return <div>{details}</div>;
  };

  optionsPage = () => {
    const { sourceType } = this.state;
    if (sourceType === null) {
      return;
    }

    const sourceSystem = migrationSources[sourceType];
    return (
      <Form
        className="migration-run-form"
        {...formLayout}
        initialValues={this.state}
        onValuesChange={this.handleFormValuesChange}
        ref={this.formRef}
      >
        {this.formOptionFields(sourceSystem)}
      </Form>
    );
  };

  handleUploadsFinished = () => {
    this.setState({ nextDisabled: false, backDisabled: false });
  };

  handleUploadInProgress = () => {
    this.setState({ nextDisabled: true, backDisabled: true });
  };

  fileUploadPage = () => {
    const { migrationFiles } = this.state;
    return (
      <MigrationFileUploader
        onFileRemoved={this.handleFileRemoved}
        onUploadComplete={this.handleFileUploaded}
        defaultFiles={migrationFiles}
        onNoUploadsInProgress={this.handleUploadsFinished}
        onUploadsInProgress={this.handleUploadInProgress}
      />
    );
  };

  steps(currentStep, sourceType): JSX.Element {
    const sourceDetails = migrationSources[sourceType];
    const steps = [
      {
        title: "Source",
        icon: <AppstoreOutlined />,
        content: this.systemListPage(),
      },
      {
        title: "Options",
        icon: <ControlOutlined />,
        content: this.optionsPage(),
      },
      {
        title: "Records",
        icon: <FileAddOutlined />,
        content: this.sourceRecordTypesPage(),
      },
      {
        title: "Review",
        icon: <ContainerOutlined />,
        content: this.reviewPage(),
      },
    ];

    if (sourceDetails && sourceDetails.requiresFileUpload) {
      steps.push({
        title: "Files",
        icon: <FileAddOutlined />,
        content: this.fileUploadPage(),
      });
    }

    steps.sort((a, b) => stepOrder[a.title] - stepOrder[b.title]);

    return (
      <div>
        <Steps
          size="small"
          current={currentStep}
          style={{ paddingBottom: "30px" }}
        >
          {steps.map((step) => (
            <Step key={step.title} title={step.title} icon={step.icon} />
          ))}
        </Steps>
        <div
          className="steps-content"
          style={{ height: 600, overflow: "auto" }}
        >
          {steps[currentStep].content}
        </div>
      </div>
    );
  }

  isSubmitDisabled = (): boolean => {
    const { recordTypes } = this.state;
    if (this.isMissingFiles(recordTypes)) {
      return true;
    } else {
      return false;
    }
  };

  modalButtons(currentStep): JSX.Element[] {
    const { sourceType, nextDisabled, backDisabled } = this.state;
    const sourceDetails = migrationSources[sourceType];
    let numSteps = 4;
    if (sourceDetails && sourceDetails.requiresFileUpload) {
      numSteps += 1;
    }

    const buttons = [
      <Button key="cancel" onClick={this.handleCancel} danger>
        Cancel
      </Button>,
    ];
    if (currentStep > 0) {
      buttons.push(
        <Button key="back" disabled={backDisabled} onClick={this.previousStep}>
          Back
        </Button>,
      );
    }
    if (currentStep > 0 && currentStep < numSteps - 1) {
      buttons.push(
        <Button
          type="primary"
          key="next"
          disabled={nextDisabled}
          onClick={this.nextStep}
        >
          Next
        </Button>,
      );
    }

    if (currentStep === numSteps - 1) {
      buttons.push(
        <Button
          key="submit"
          type="primary"
          onClick={this.handleOk}
          disabled={this.isSubmitDisabled()}
          loading={this.state.confirmLoading}
        >
          Submit
        </Button>,
      );
    }
    return buttons;
  }

  render() {
    const { visible, confirmLoading, currentStep, sourceType } = this.state;
    const steps = this.steps(currentStep, sourceType);

    return (
      <div style={this.props.style}>
        <Button
          type="primary"
          style={this.props.buttonStyle}
          onClick={this.showModal}
        >
          {this.props.buttonText || "Create New Migration Run"}
        </Button>
        <Modal
          wrapClassName="create-migration-run-modal"
          title={this.state.modalTitle}
          open={visible}
          confirmLoading={confirmLoading}
          styles={{ body: { height: 700 } }}
          onCancel={this.handleCancel}
          width={550}
          footer={this.modalButtons(currentStep)}
        >
          {steps}
        </Modal>
      </div>
    );
  }
}
